import React, { useContext, useState } from "react";
import "./docOverlay.css";
import "./feedbackForm.css";
import { AppBar } from "./appBar";
import { PageHeading } from "./pageHeading";
import { DataLayerContext } from "../../dataLayer/DataLayer";
import { type FeedbackDiagnostics } from "../../diagnostics";

export interface IFeedbackFormProps {
  open: boolean;
  onClose: () => void;
  initialSentiment?: Sentiment;
  initialMessage?: string;
}

type Status = "idle" | "sending" | "sent" | "error" | "ratelimited" | "notsignedin";
type Sentiment = "positive" | "negative" | "feature-request";

/** In-app feedback form. Files the feedback as a GitHub issue via the backend
 *  (see DataLayer.submitFeedback) instead of popping out to email. */
export function FeedbackForm({
  open,
  onClose,
  initialSentiment = "positive",
  initialMessage = "",
}: IFeedbackFormProps) {
  const dataLayer = useContext(DataLayerContext);
  const [sentiment, setSentiment] = useState<Sentiment>(initialSentiment);
  const [message, setMessage] = useState<string>(initialMessage);
  const [status, setStatus] = useState<Status>("idle");
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [diagnostics, setDiagnostics] = useState<FeedbackDiagnostics>();
  const [diagnosticsError, setDiagnosticsError] = useState(false);
  const [diagnosticsAttempt, setDiagnosticsAttempt] = useState(0);

  React.useEffect(() => {
    if (open) {
      setSentiment(initialSentiment);
      setMessage(initialMessage);
      setStatus("idle");
      setIncludeDiagnostics(true);
      setDiagnostics(undefined);
      setDiagnosticsError(false);
    }
  }, [open, initialSentiment, initialMessage]);

  React.useEffect(() => {
    if (!open || sentiment !== "negative" || !includeDiagnostics || diagnostics) {
      return;
    }
    let cancelled = false;
    setDiagnosticsError(false);
    dataLayer.getFeedbackDiagnostics().then(
      (snapshot) => {
        if (!cancelled) setDiagnostics(snapshot);
      },
      (error: unknown) => {
        console.error("Failed to prepare feedback diagnostics", error);
        if (!cancelled) setDiagnosticsError(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, sentiment, includeDiagnostics, diagnostics, diagnosticsAttempt, dataLayer]);

  if (!open) {
    return null;
  }

  const reset = () => {
    setSentiment(initialSentiment);
    setMessage("");
    setStatus("idle");
  };

  const close = () => {
    reset();
    onClose();
  };

  const onSend = async () => {
    const attachDiagnostics = sentiment === "negative" && includeDiagnostics;
    if (!message.trim() || status === "sending" || (attachDiagnostics && !diagnostics)) {
      return;
    }
    setStatus("sending");
    try {
      const result = await dataLayer.submitFeedback(
        sentiment,
        message.trim(),
        attachDiagnostics ? diagnostics : undefined,
      );
      if (result === "Success") {
        setStatus("sent");
      } else if (result === "RateLimited") {
        setStatus("ratelimited");
      } else if (result === "NotSignedIn") {
        setStatus("notsignedin");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Failed to submit feedback", error);
      setStatus("error");
    }
  };

  return (
    <div className="doc-overlay" role="dialog" aria-modal="true" aria-label="Send feedback">
      <div className="screen doc">
        <AppBar onBack={close} />
        <PageHeading eyebrow={"We\u2019re listening"} title="Send feedback" />

        <div className="doc__body">
          {status === "sent" ? (
            <div className="fb__sent">
              <div className="fb__sent-title">Thanks for the feedback!</div>
              <p className="fb__sent-body">
                It went straight to the team&rsquo;s to-do list. We read every note, it really does
                shape the game.
              </p>
              <button className="btn btn--primary fb__cta" onClick={close}>
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="fb__lede">
                How&rsquo;s it going? Tell us what you love or what&rsquo;s off.
              </p>

              <div className="fb__choices" role="radiogroup" aria-label="Sentiment">
                <button
                  type="button"
                  role="radio"
                  aria-checked={sentiment === "positive"}
                  disabled={status === "sending"}
                  className={`fb__choice${sentiment === "positive" ? " selected" : ""}`}
                  onClick={() => setSentiment("positive")}
                >
                  <span className="fb__choice-emoji" aria-hidden="true">
                    &#x1F499;
                  </span>
                  I&rsquo;m loving it
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={sentiment === "negative"}
                  disabled={status === "sending"}
                  className={`fb__choice${sentiment === "negative" ? " selected" : ""}`}
                  onClick={() => setSentiment("negative")}
                >
                  <span className="fb__choice-emoji" aria-hidden="true">
                    &#x1F41B;
                  </span>
                  Report a bug
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={sentiment === "feature-request"}
                  disabled={status === "sending"}
                  className={`fb__choice${sentiment === "feature-request" ? " selected" : ""}`}
                  onClick={() => setSentiment("feature-request")}
                >
                  <span className="fb__choice-emoji" aria-hidden="true">
                    &#x1F4A1;
                  </span>
                  Feature request
                </button>
              </div>

              <textarea
                className="fb__textarea"
                placeholder={
                  sentiment === "positive"
                    ? "What do you like? Any ideas to make it even better?"
                    : sentiment === "feature-request"
                      ? "What would you like to see in Wirder?"
                      : "What happened? The more detail, the faster we can fix it."
                }
                value={message}
                maxLength={5000}
                disabled={status === "sending"}
                onChange={(e) => setMessage(e.target.value)}
              />

              {sentiment === "negative" && (
                <div className="fb__diagnostics">
                  <label className="fb__diagnostics-toggle">
                    <input
                      type="checkbox"
                      checked={includeDiagnostics}
                      disabled={status === "sending"}
                      onChange={(e) => setIncludeDiagnostics(e.target.checked)}
                    />
                    Include diagnostic information
                  </label>
                  <p>
                    {includeDiagnostics
                      ? "This bug report will include app and device details, the current screen and puzzle date, and recent errors and subscription-management activity to help us investigate. You can review them below or turn this off."
                      : "Diagnostic information will not be sent with this report."}
                  </p>
                  {includeDiagnostics && (
                    <>
                      {diagnostics ? (
                        <details>
                          <summary>View diagnostic information</summary>
                          <pre>{JSON.stringify(diagnostics, null, 2)}</pre>
                        </details>
                      ) : diagnosticsError ? (
                        <div className="fb__msg fb__msg--error" role="alert">
                          Could not prepare diagnostics. Try again or turn them off to send.
                          <button
                            type="button"
                            onClick={() => setDiagnosticsAttempt((attempt) => attempt + 1)}
                          >
                            Try again
                          </button>
                        </div>
                      ) : (
                        <p role="status">Preparing diagnostic information...</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {status === "error" && (
                <div className="fb__msg fb__msg--error">
                  Couldn&rsquo;t send that just now. Please try again.
                </div>
              )}
              {status === "ratelimited" && (
                <div className="fb__msg fb__msg--error">
                  You&rsquo;ve sent a lot of feedback recently&mdash;thank you! Please try again
                  later.
                </div>
              )}
              {status === "notsignedin" && (
                <div className="fb__msg fb__msg--error">
                  You need to be signed in to send feedback.
                </div>
              )}

              <button
                className="btn btn--primary fb__cta"
                onClick={onSend}
                disabled={
                  !message.trim() ||
                  status === "sending" ||
                  (sentiment === "negative" && includeDiagnostics && !diagnostics)
                }
              >
                {status === "sending" ? "Sending\u2026" : "Send feedback"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
